import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { api } from '../services/api';
import { AuditLog } from '../types';
import { Download, Search, Calendar } from 'lucide-react';
import { format } from 'date-fns';
import './AuditLogs.css';

const AuditLogs: React.FC = () => {
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const { data, isLoading } = useQuery(
    ['audit-logs', search, startDate, endDate],
    async () => {
      const params = new URLSearchParams();
      if (search) params.append('action', search);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      const response = await api.get(`/audit?${params.toString()}`);
      return response.data;
    }
  );

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      const response = await api.get(`/audit/export?${params.toString()}`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `audit-logs-${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error: any) {
      console.error('Export failed:', error);
    }
  };

  const logs: AuditLog[] = data?.logs || [];

  return (
    <div className="audit-logs-page">
      <div className="page-header">
        <div>
          <h1>Audit Logs</h1>
          <p>Track all system activities and access</p>
        </div>
        <button className="btn-primary" onClick={handleExport}>
          <Download size={20} />
          <span>Export CSV</span>
        </button>
      </div>

      <div className="filters">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search by action..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="date-filters">
          <div className="date-input">
            <Calendar size={20} />
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              placeholder="Start Date"
            />
          </div>
          <div className="date-input">
            <Calendar size={20} />
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              placeholder="End Date"
            />
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="loading">Loading audit logs...</div>
      ) : logs.length === 0 ? (
        <div className="empty-state">No audit logs found</div>
      ) : (
        <div className="audit-table-container">
          <table className="audit-table">
            <thead>
              <tr>
                <th>Timestamp</th>
                <th>User</th>
                <th>Action</th>
                <th>Resource</th>
                <th>IP Address</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log.id}>
                  <td>{format(new Date(log.createdAt), 'MMM dd, yyyy HH:mm:ss')}</td>
                  <td>
                    {log.user ? (
                    <div className="user-cell">
                      <div className="user-name">{log.user.username}</div>
                      <div className="user-email">{log.user.email}</div>
                    </div>
                    ) : (
                      <span className="text-muted">System / Guest</span>
                    )}
                  </td>
                  <td>
                    <span className="action-badge">{log.action}</span>
                  </td>
                  <td>
                    {log.resourceType && log.resourceId ? (
                      <span>
                        {log.resourceType} ({log.resourceId.substring(0, 8)}...)
                      </span>
                    ) : (
                      <span className="text-muted">-</span>
                    )}
                  </td>
                  <td>{log.ipAddress || '-'}</td>
                  <td>
                    {log.details ? (
                      <details className="details-dropdown">
                        <summary>View</summary>
                        <pre>{JSON.stringify(JSON.parse(log.details), null, 2)}</pre>
                      </details>
                    ) : (
                      '-'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AuditLogs;

